import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { mfaStatusQuery, sessionApi, sessionQuery } from '../api/session';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import {
  Alert,
  Button,
  Card,
  DevCode,
  Loading,
  PageHeader,
  RecoveryCodes,
  TextField,
} from '@avrahami/ui';

export function AccountPage() {
  const session = useQuery(sessionQuery);
  const me = session.data?.kind === 'signed-in' ? session.data.me : undefined;
  return (
    <>
      <PageHeader title={he.account.title} />
      {me === undefined ? null : (
        <p className="mb-6 text-sm text-slate-600">
          {me.user.displayName} · <span className="ltr">{me.user.email}</span>
          {me.platform === null ? null : ` · ${he.roles[me.platform.role]}`}
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <ChangePassword />
        <SecondFactor />
      </div>
    </>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const change = useMutation({
    mutationFn: sessionApi.changePassword,
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    setMismatch(next !== confirm);
    if (next === confirm) change.mutate({ currentPassword: current, newPassword: next });
  };
  return (
    <Card title={he.account.password}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <input type="text" name="username" autoComplete="username" hidden readOnly />
        <TextField
          label={he.account.current}
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(event) => {
            setCurrent(event.target.value);
          }}
        />
        <TextField
          label={he.account.next}
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={next}
          onChange={(event) => {
            setNext(event.target.value);
          }}
        />
        <TextField
          label={he.account.confirm}
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={confirm}
          error={mismatch ? he.invitation.mismatch : undefined}
          onChange={(event) => {
            setConfirm(event.target.value);
          }}
        />
        {change.isSuccess ? <Alert tone="success">{he.account.changed}</Alert> : null}
        {change.isError ? <Alert>{errorMessage(change.error)}</Alert> : null}
        <div>
          <Button type="submit" busy={change.isPending}>
            {he.common.save}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function SecondFactor() {
  const status = useQuery(mfaStatusQuery);
  const [code, setCode] = useState('');
  const regenerate = useMutation({
    mutationFn: sessionApi.regenerateRecoveryCodes,
    onSuccess: () => {
      setCode('');
      void status.refetch();
    },
  });
  if (status.isPending) return <Loading />;
  if (status.isError) return <Alert>{errorMessage(status.error)}</Alert>;

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    regenerate.mutate(code.trim());
  };
  return (
    <Card title={he.account.mfa}>
      <div className="flex flex-col gap-4 text-sm">
        <p>{status.data.enabled ? he.account.mfaOn : he.account.mfaOff}</p>
        {status.data.enabled ? (
          <>
            <p>
              {he.account.remaining} {status.data.recoveryCodesRemaining}
            </p>
            {regenerate.data === undefined ? (
              <form onSubmit={onSubmit} className="flex flex-col gap-3">
                <p className="text-slate-600">{he.account.regenerateHint}</p>
                <DevCode
                  code={status.data.devCode}
                  onUse={setCode}
                  label={he.devCode.label}
                  useLabel={he.devCode.use}
                />
                <TextField
                  label={he.mfaSetup.code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value);
                  }}
                  className="ltr"
                />
                {regenerate.isError ? <Alert>{errorMessage(regenerate.error)}</Alert> : null}
                <div>
                  <Button type="submit" tone="secondary" busy={regenerate.isPending}>
                    {he.account.regenerate}
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <p className="text-slate-600">{he.mfaSetup.recoveryIntro}</p>
                <RecoveryCodes
                  codes={regenerate.data.recoveryCodes}
                  label={he.mfaSetup.recoveryTitle}
                />
              </>
            )}
          </>
        ) : null}
      </div>
    </Card>
  );
}
